import type { LucideIcon } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn.js';
import { Skeleton } from '../shadcn/index.js';

export type StatTone = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE_COLOR: Record<StatTone, string> = {
  brand: 'var(--color-brand)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  neutral: 'var(--color-fg-muted)',
};

export interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Show a skeleton in place of the value while data loads. */
  loading?: boolean;
  icon?: LucideIcon;
  /** Semantic tone → accent color. Ignored if `accent` is given. */
  tone?: StatTone;
  /** Explicit accent color (e.g. "var(--viz-1)"), overrides `tone`. */
  accent?: string;
  /** Signed delta vs. a comparison period; coloured by sign, shown as a pill. */
  delta?: { value: string; positive: boolean } | null;
  /** Secondary caption under the value. */
  hint?: string;
  /** Makes the whole card a router link with hover/focus affordance. */
  to?: string;
  className?: string;
}

/**
 * Interactive KPI / stat surface used on the dashboard and report headers.
 * The accent (tone or explicit colour) drives the icon chip, a soft corner
 * glow, and a bottom bar that sweeps in on hover. Hover/keyboard-focus lift
 * the card and deepen its shadow; colour-carrying bits use an inline
 * `--accent` var while the animated bits use transforms/opacity so they work
 * regardless of the active palette + theme. RTL-safe (logical insets,
 * centre-origin bar).
 */
export function StatCard({
  label,
  value,
  loading,
  icon: Icon,
  tone = 'neutral',
  accent,
  delta,
  hint,
  to,
  className,
}: StatCardProps): JSX.Element {
  const style = { '--accent': accent ?? TONE_COLOR[tone] } as CSSProperties;

  const card = (
    <div
      className={cn(
        'glass-panel relative h-full overflow-hidden rounded-xl border p-4 shadow-sm',
        'transition-[transform,box-shadow,border-color] duration-200 ease-out',
        'group-hover:-translate-y-0.5 group-hover:shadow-lg',
        'group-focus-visible:-translate-y-0.5 group-focus-visible:shadow-lg',
        'group-focus-visible:ring-2 group-focus-visible:ring-brand/40',
        className,
      )}
      style={style}
    >
      {/* Soft accent glow in the inline-end top corner; brightens on hover. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-10 h-28 w-28 rounded-full opacity-50 blur-2xl transition-opacity duration-300 group-hover:opacity-90"
        style={{
          insetInlineEnd: '-2.5rem',
          background:
            'radial-gradient(circle, color-mix(in oklab, var(--accent) 55%, transparent), transparent 70%)',
        }}
      />

      <div className="relative flex items-start justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-fg-subtle">
          {label}
        </span>
        {Icon && (
          <span
            className="grid h-9 w-9 flex-none place-items-center rounded-lg transition-transform duration-200 group-hover:scale-110"
            style={{
              background: 'color-mix(in oklab, var(--accent) 16%, transparent)',
              color: 'var(--accent)',
            }}
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden />
          </span>
        )}
      </div>

      <div className="relative mt-3 font-mono text-[26px] font-extrabold leading-tight tracking-tight tabular-nums text-fg">
        {loading ? <Skeleton className="h-7 w-28" /> : value}
      </div>

      {(delta || hint) && (
        <div className="relative mt-1.5 flex items-center gap-2 text-xs">
          {delta && (
            <span
              className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold"
              style={{
                background: `color-mix(in oklab, ${
                  delta.positive ? 'var(--color-success)' : 'var(--color-danger)'
                } 16%, transparent)`,
                color: delta.positive ? 'var(--color-success)' : 'var(--color-danger)',
              }}
            >
              {delta.positive ? '▲' : '▼'} {delta.value}
            </span>
          )}
          {hint && <span className="text-fg-muted">{hint}</span>}
        </div>
      )}

      {/* Accent bar sweeps out from centre on hover (direction-neutral). */}
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[3px] origin-center scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100"
        style={{
          background:
            'linear-gradient(90deg, color-mix(in oklab, var(--accent) 30%, transparent), var(--accent), color-mix(in oklab, var(--accent) 30%, transparent))',
        }}
      />
    </div>
  );

  return to ? (
    <Link to={to} className="group block h-full rounded-xl outline-none">
      {card}
    </Link>
  ) : (
    <div className="group h-full">{card}</div>
  );
}
